import { useCallback, useEffect, useRef, useState } from "react";
import { useAppServices } from "hook/services";

const defaultHomeSectionContent = {
  showBusinessesByTagOnHome: true,
  showBlogsOnHome: true,
  showFeaturedListingOnHome: true,
  showHeroSection: true,
  showSiteDescription: true,
  showFeaturedListingsSlider: true,
  showFreeListings: true,
  showEvents: true,
  showJobs: true,
  showNews: true,
  showHomepageOptinOnHome: true,
};

function useAgencyTheme(agencyId) {
  const Service = useAppServices();
  const serviceRef = useRef(Service);
  serviceRef.current = Service;

  const [selectedTheme, setSelectedTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const themeRef = useRef(null);
  themeRef.current = selectedTheme;

  const loadTheme = useCallback(async () => {
    if (!agencyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { response } = await serviceRef.current.themes.get({
        query: `theme_type=agency&agency_id=${agencyId}`,
      });
      setSelectedTheme(response?.data?.[0] || null);
    } catch (error) {
      console.error("Failed to load theme for onboarding", error);
      setSelectedTheme(null);
    } finally {
      setLoading(false);
    }
  }, [agencyId]);

  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  const handelThemeData = useCallback(
    async (data) => {
      const current = themeRef.current;
      if (!current?.theme_id) {
        return { success: false, message: "Theme is not configured for this agency." };
      }

      setButtonLoading(true);
      try {
        const submitData = { ...current };
        if (submitData.theme_data?.domain) {
          submitData.theme_data.domain.domain_update = false;
        }

        const updatedThemeData = {
          ...submitData.theme_data,
          general: {
            ...submitData.theme_data?.general,
            ...data?.general,
          },
          content: {
            ...defaultHomeSectionContent,
            ...(submitData.theme_data?.content || {}),
            ...(data?.content || {}),
          },
          ...Object.keys(data || {}).reduce((acc, key) => {
            if (key !== "general" && key !== "content") {
              acc[key] = data[key];
            }
            return acc;
          }, {}),
        };

        const logoFields = ["logo", "Meta_logo", "footer_logo", "auth_logo"];
        for (const field of logoFields) {
          const value = updatedThemeData?.general?.[field];
          if (value && !String(value).includes("https")) {
            return { success: false, message: "Error while uploading logo" };
          }
        }

        const payload = {
          agency_id: agencyId,
          theme_data: updatedThemeData,
          theme_type: "agency",
          theme_id: current.theme_id,
        };
        const { response, error } = await serviceRef.current.themes.updateOrCreateByAgencyId({
          payload,
        });

        if (response?.success) {
          const next = { ...current, theme_data: updatedThemeData };
          themeRef.current = next;
          setSelectedTheme(next);
          return { success: true, message: "Theme Updated Successfully" };
        }

        return { success: false, message: error?.message || "Failed to update theme" };
      } catch (error) {
        if (error?.response?.status === 504 || error?.message?.includes("Network Error") || error?.code === "ECONNABORTED") {
          return { success: true, message: "Theme Updated Successfully" };
        }
        return { success: false, message: error?.message || "Failed to update theme" };
      } finally {
        setButtonLoading(false);
      }
    },
    [agencyId]
  );

  return {
    selectedTheme,
    loading,
    buttonLoading,
    handelThemeData,
    reloadTheme: loadTheme,
  };
}

export default useAgencyTheme;
